import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import find from 'lodash-es/find';
import map from 'lodash-es/map';
import { Subject, Subscription } from 'rxjs';
import { NgInject } from '@df/ng/ng-inject';
import { App } from '@df/core/app';

export interface ILoggerEvent {
    level: number;
    title?: string;
    args: any[];
    stack?: string;
}

@Injectable({
    providedIn: 'root'
})
export class Logger {
    protected subject = new Subject<ILoggerEvent>();

    private router = inject(Router);

    constructor(private app: App) {
        app.logger = this;
    }

    subscribe(...args): Subscription {
        return this.subject.subscribe(...args);
    }

    private canPrintOutput() {
        return this.app.acl.isDeveloper || !this.app.isPlatformBrowser;
    }

    protected consoleGroup(type = 'log', ...messages) {
        if (this.canPrintOutput()) {
            switch (type) {
                case 'info':
                    console.group(
                        `%c[INFO] %cDeveloper only message (${this.router.url}) ↴`,
                        'color: blue; font-weight: bold',
                        'color: grey'
                    );
                    // eslint-disable-next-line no-restricted-syntax
                    console.info(...messages);
                    break;
                case 'warn':
                    console.group(
                        `%c[WARN] %cDeveloper only message (${this.router.url}) ↴`,
                        'color: orange; font-weight: bold',
                        'color: grey'
                    );
                    console.log(...messages);
                    break;
                case 'error':
                    console.group(
                        `%c[ERROR] %cDeveloper only message (${this.router.url}) ↴`,
                        'color: red; font-weight: bold',
                        'color: grey'
                    );
                    console.error(...messages);
                    break;
                default:
                    console.group(
                        `%c[LOG] %cDeveloper only message (${this.router.url}) ↴`,
                        'color: gray; font-weight: bold',
                        'color: grey'
                    );
                    console.log(...messages);
            }
            console.groupEnd();
        }
    }

    info(...args: any[]) {
        this.consoleGroup('info', ...args, '\r\n');

        this.subject.next({
            level: 2,
            args
        });
    }

    log(...args: any[]) {
        this.consoleGroup('log', ...args, '\r\n');

        this.subject.next({
            level: 5,
            args
        });
    }

    error(...args: any[]) {
        const error = find(args, arg => arg instanceof Error);
        const msgs = map(args, arg => (arg instanceof Error ? arg.message : arg));
        let stack: string | undefined;

        if (error) {
            stack = error.stack;
        } else {
            stack = new Error().stack;

            if (stack) {
                stack = stack.replace(/^[^\n]*\n[^\n]*\n/, '') + '\n';
            }
        }

        this.consoleGroup('error', '[ERROR] ', ...msgs, '\r\n\r\n' + 'Stack:', stack, '\r\n');

        this.subject.next({
            level: 3,
            args,
            stack
        });
    }

    warn(...args: any[]) {
        this.consoleGroup('warn', '[WARN] ', ...args, '\r\n');

        this.subject.next({
            level: 4,
            args
        });
    }

    debug(title: string, ...args: any[]) {
        this.subject.next({
            level: 4,
            title,
            args
        });
    }

    onError(sourceType: string, err: Error | string) {
        this.error('ERROR IN: [' + sourceType + ']', err);
    }
}

declare module '@df/core/app' {
    interface App {
        logger: Logger;
    }
}
